/**
 * Which successful writes can change what a capacity view shows.
 *
 * The HTTP layer announces every mutation (`lib/api-write-events`); capacity
 * owns the decision about which ones matter, so the write signal stays free of
 * feature knowledge. Matching on the URL keeps this a pure, testable predicate.
 */

/**
 * Path fragments whose writes move a person's allocations, capacity, or
 * actuals. Anything not listed here (labels, states, comments, dashboards,
 * preferences…) leaves the timeline unchanged and must not trigger a re-read —
 * these reads are rollup-backed and not cheap.
 */
const CAPACITY_WRITE_FRAGMENTS = [
  // Allocations: a work item's dates, assignees, or estimate — under either the
  // project-scoped or the workspace-scoped route, plus the bulk date endpoint,
  // sub-items, and the legacy `issues` alias.
  "/work-items",
  "/work-item-dates/",
  "/sub-work-items/",
  "/issues",
  // Calendar events consume capacity alongside tasks.
  "/events/",
  // Capacity itself: contracted work hours and booked time off.
  "/work-hours/",
  "/time-off",
  // The actuals overlay (logged vs estimated).
  "/worklogs/",
  // Bulk edits/deletes land through the async bulk-operations endpoint.
  "/bulk-operations/",
];

/**
 * True when a successful write to `url` may change a capacity view's data.
 *
 * Deliberately errs toward the cheap side of the trade: an unmatched URL means
 * no refetch, so the list above is what keeps a capacity view honest. A false
 * positive costs one coalesced re-read; a false negative shows the user stale
 * allocations, which is the bug this exists to prevent.
 */
export function writeAffectsCapacity(url: string): boolean {
  // Capacity's own reads are GETs, so they never reach here — but an export or
  // any other capacity-allocations POST must not feed itself a refresh loop.
  if (url.includes("/capacity-allocations")) return false;
  return CAPACITY_WRITE_FRAGMENTS.some((fragment) => url.includes(fragment));
}
